import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { Header, Icon, Input, Segment } from "semantic-ui-react";

// Interface
import { CartData, CartItemCard, CartPaymentLinkCard, toWholeQuantity } from "./StaffChatInterface";

// Utils
import formatComma from "react-lib/utils/formatComma";
import ModConfirmCustom from "./ModConfirmCustom";

import "./CartSection.scss";

const PRIMARY_COLOR = "var(--primary-theme-color)";

// like MeHealthHome (ishealth-main): debounce แล้วค่อยยิงค้น
// 400 → 300ms: panel เล็ก staff พิมพ์สั้น ๆ (ชื่อแพ็กเกจ/รหัส) รอไม่ไหวกู้
const SEARCH_DEBOUNCE_MS = 300;
const SKELETON_ROWS = 4;

// like MeHealthHome getThumbnailImage: image_thumbnail → image_hero → image
const getResultImage = (item: any): string => {
  const image = item?.image_thumbnail || item?.image_hero || item?.image || "";

  if (!image) return "";
  if (typeof image === "string") return image;

  return image.url || image.image || image.file || "";
};

// ─── ข้อความ error ในบรรทัดรายการตรวจ ─────────────────────────────
// staff ไม่จำเป็นต้องอ่าน error จากระบบ (DRF / 500 / traceback) → แปลงเป็นไทยสั้น ๆ
// ข้อความสั้นที่อ่านรู้อยู่แล้วแสดงครบ / ยาวหรือเป็น error เชิงเทคนิค → ตัด + ปุ่ม "รายละเอียด" (เก็บ raw ไว้ให้ staff ดูกรณีต้องติดต่อทีมระบบ)
const CART_ERROR_MAX_LENGTH = 90;
const CART_ERROR_FALLBACK = "ทำรายการไม่สำเร็จ กรุณาลองอีกครั้ง";

// เรียงจาก error เชิงเทคนิค (เจอบ่อยใน log ระบบ) → เงื่อนไขธุรกิจ
const CART_ERROR_HINTS: { match: RegExp; text: string }[] = [
  {
    match: /timeout|network|err_|\b50[0-9]\b|bad gateway|internal|server|exception|traceback/i,
    text: "ระบบขัดข้องชั่วคราว กรุณาลองอีกครั้ง",
  },
  {
    match: /token|authentication|\b401\b|session|login/i,
    text: "การเข้าสู่ระบบหมดอายุ กรุณาลองอีกครั้ง",
  },
  {
    match: /permission|forbidden|\b403\b|not allowed|สิทธิ์/i,
    text: "บัญชีนี้ไม่มีสิทธิ์ทำรายการนี้",
  },
  {
    match: /\bquantity\b|insufficient|not enough|จำนวนคงเหลือ/i,
    text: "จำนวนคงเหลือไม่พอ",
  },
  // error จาก API จริงมักเป็น "Can't find age from patient" (ishealth-main ใช้ isAgeMissingError)
  // ต้องตรวจก่อน hint เรื่องอายุทั่วไป ไม่งั้นจะกลายเป็น "ไม่เหมาะกับอายุ" ทั้งที่จริงคือยังไม่รู้อายุ
  {
    match: /can'?t find age|cannot find age|find age from|age[^a-z]{0,20}(not found|missing)/i,
    text: "ผู้รับบริการยังไม่มีข้อมูลอายุ กรุณาบันทึกอายุให้ก่อน",
  },
  { match: /อายุ|\bage\b/i, text: "รายการนี้ไม่เหมาะกับอายุของผู้รับบริการ" },
  { match: /เพศ|\bgender\b/i, text: "รายการนี้ไม่เหมาะกับเพศของผู้รับบริการ" },
  { match: /แผนก|\bdivision\b/i, text: "รายการนี้ไม่ได้เปิดขายในแผนกนี้" },
];

// error ที่เข้าข่าย "จากระบบ" (HTML/JSON/traceback/SQL) — ไม่ควรโชว์ดิบให้ user ทั่วไป
const SYSTEM_ERROR_PATTERN =
  /<\/?(html|body|div|span|p|pre|code)\b|traceback|non_field_errors|TypeError|ReferenceError|undefined is not|\{[\s\S]*"\w*"\s*:|\bSELECT\b[\s\S]*\bFROM\b/i;

const getCartErrorText = (rawError: any) => {
  const raw = String(rawError ?? "")
    .replace(/\s+/g, " ")
    .trim();

  if (!raw) {
    return null;
  }

  const isSystemError = SYSTEM_ERROR_PATTERN.test(raw) || !/[\u0E00-\u0E7F]/.test(raw);
  // ข้อความสั้นที่เป็นไทยและ staff อ่านรู้อยู่แล้ว → แสดงตามเดิม ไม่ต้องแทนด้วย hint
  const useHint = isSystemError || raw.length > CART_ERROR_MAX_LENGTH;
  const hint = useHint ? CART_ERROR_HINTS.find((item) => item.match.test(raw)) : null;

  const text = hint ? hint.text : raw;
  const isLong = raw.length > CART_ERROR_MAX_LENGTH;
  const shortText =
    text.length > CART_ERROR_MAX_LENGTH
      ? `${text.slice(0, CART_ERROR_MAX_LENGTH).trim()}...`
      : text;

  // raw ที่ถูกแทน/ถูกตัด เก็บไว้ให้ staff กด "รายละเอียด" ดูได้
  const detail = hint || isLong ? raw : null;

  return { text: shortText, detail };
};

const styles = {
  sectionHeader: {
    background: PRIMARY_COLOR,
    padding: "8px 13px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    margin: 0,
  } as CSSProperties,
} as const;

export type CartSectionProps = {
  cartData?: CartData;
  patientId?: number;
  isCollapsed: boolean;
  onToggle: () => void;
  isLoading?: boolean;
  addLoadingStatus?: Record<string, any>;
  errorMessage?: Record<string, any>;
  // refresh = true ข้าม cache ของ cartData (ดู GetCartPackageList) เพื่อดึงราคา/รายการใหม่
  onSearchPackages: (keyword: string, refresh?: boolean) => void;
  onAddPackage: (packageId: number, item?: any) => void;
  onDecreasePackage: (packageId: number, item?: any) => void;
  // โหลดรายการหน้าถัดไปของ keyword เดิม (API limit/offset)
  onLoadMorePackages: (keyword: string, offset: number) => void;
  // ส่ง link จ่ายเงิน (mehealthCheckout) เข้าแชทของลูกค้า — คืน { link } เมื่อ post สำเร็จ
  // post เข้า chat channel ของลูกค้า — panel ไม่แสดง link ซ้ำ อ่านผลจาก loadingStatus/errorMessage
  onSendCartPaymentLink: () => Promise<any>;
  isSendingPaymentLink?: boolean;
};

const CartSection: React.FC<CartSectionProps> = (props) => {
  const [searchValue, setSearchValue] = useState("");
  const [errorDetailKey, setErrorDetailKey] = useState<string | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showCartItems, setShowCartItems] = useState(false);
  // link ที่ส่งสำเร็จจะไปอยู่ในแชทของลูกค้าเลย — panel ไม่ต้องแสดง/ไม่ต้องคัดลอกซ้ำ
  // สถานะมีแค่ loading (loadingStatus[GET_CART_PAYMENT_LINK]) กับ error ที่แสดงในบรรทัดนี้
  // กันมือลั่น: modal confirm ก่อนส่งเข้าแชท (Modal เดียวกับที่ StaffChat ใช้ยืนยัน leave group)
  const [confirmSendLink, setConfirmSendLink] = useState(false);

  const handleSendPaymentLink = () => {
    setConfirmSendLink(false);

    return props.onSendCartPaymentLink();
  };

  // Debounce search input — like MeHealthHome บรรทัด 416-423
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchValue.trim()), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [searchValue]);

  // ยิงค้น (keyword เปล่า = รายการทั้งหมดของแผนก) — สลับคนไข้/สลับแชทต้องโหลดใหม่
  useEffect(() => {
    props.onSearchPackages(debouncedSearch);
  }, [debouncedSearch, props.patientId]);

  // dedup รายการที่ API คืนมาซ้ำกัน (เหมือน MeHealthHome บรรทัด 236-249) แต่ไม่จัดกลุ่มอื่นเพิ่ม
  const packageItems = useMemo(() => {
    const seen = new Set<string>();
    const result: any[] = [];

    for (const item of props.cartData?.packageList ?? []) {
      if (!item) continue;

      const key = String(item.id ?? item.package_id ?? item.product_id ?? item.name);
      if (seen.has(key)) continue;

      seen.add(key);
      result.push(item);
    }

    return result;
  }, [props.cartData?.packageList]);

  const isLoading = !!props.isLoading;

  // cartData ที่โหลดไว้ให้คนไข้คนก่อนหน้า (ตอนสลับแชท request ใหม่ยังไม่กลับ)
  // ถ้า cartPatient ไม่ตรงกับแชทที่เปิดอยู่ตอนนี้ = ข้อมูลคนละคน ใช้นับ badge/stepper ไม่ได้
  const isCartOfCurrentPatient =
    !props.cartData?.cartPatient || Number(props.cartData?.cartPatient) === Number(props.patientId);
  const cartItems = isCartOfCurrentPatient ? props.cartData?.cartItems || [] : [];
  const cartCount = isCartOfCurrentPatient ? props.cartData?.cartCount || 0 : 0;

  // จำนวนที่ staff เพิ่มให้ package นี้ (cartItems = PackageOrder ที่ยังไม่ชำระ)
  // 1 package อาจมีได้หลาย order → รวม quantity ไว้แสดงเป็นตัวเลขเดียว
  const cartQtyByProduct = useMemo(() => {
    const map = new Map<string, number>();

    for (const item of cartItems) {
      const key = String(item.product ?? item.package_id ?? item.product_id ?? "");
      if (!key || key === "undefined" || key === "null") continue;

      map.set(key, (map.get(key) || 0) + toWholeQuantity(item.quantity));
    }

    return map;
  }, [cartItems]);
  // price ของ PackageOrderWithItemList เป็นจำนวนของ order นั้นแล้ว (ดู PaymentWaiting ของ ishealth-main
  // ที่ sum item.price ตรง ๆ) จึงไม่คูณ quantity ซ้ำ
  const cartTotal = cartItems.reduce(
    (sum: number, item: any) => sum + (parseFloat(item.price) || 0),
    0,
  );

  // error ของปุ่มส่งลิงก์ชำระเงิน (card เดียวกับ StaffChatInterface.GetCartPaymentLink)
  const paymentError = getCartErrorText(props.errorMessage?.[CartPaymentLinkCard]);

  const handleClearSearch = () => {
    setSearchValue("");
    setDebouncedSearch("");
  };

  return (
    <div className="msg-cart-section">
      <Segment inverted style={styles.sectionHeader} onClick={props.onToggle}>
        <Header as="h5" inverted style={{ margin: 0, fontSize: "13px" }}>
          <Icon name="search" />
          ตะกร้าสินค้า ({cartCount})
        </Header>
        <Icon name={props.isCollapsed ? "angle down" : "angle up"} style={{ margin: 0 }} />
      </Segment>

      {!props.isCollapsed && (
        <div className="msg-cart-body">
          <Input
            className="msg-cart-search"
            fluid
            icon="search"
            placeholder="ค้นหาชื่อรายการตรวจ / แพ็กเกจ"
            value={searchValue}
            loading={isLoading}
            onChange={(e: any) => setSearchValue(e.target.value)}
            action={
              searchValue
                ? {
                    icon: "cancel",
                    color: "grey",
                    onClick: handleClearSearch,
                  }
                : undefined
            }
          />

          {/* like SearchResultSection บรรทัด 36-41 */}
          <div className="mh-search-results-header">
            <span>รายการตรวจทั้งหมด ({packageItems.length})</span>

            {/* ลิสต์ของคนไข้+คำค้นเดิมถูก cache ไว้ (เปิด tab กลับมาซ้ำ = ขึ้นทันทีไม่ต้องรอ API)
                staff ยังกดดึงราคา/รายการตรวจรุ่นใหม่ล่าสุดได้จากปุ่มนี้ */}
            <button
              type="button"
              className="mh-search-refresh"
              disabled={isLoading}
              onClick={() => props.onSearchPackages(debouncedSearch, true)}
            >
              <Icon
                name={isLoading ? "circle notched" : "refresh"}
                loading={isLoading}
                style={{ margin: 0 }}
              />
              รีเฟรช
            </button>
          </div>

          {/* .is-loading-more = มีข้อมูลอยู่แล้วกำลังโหลดเพิ่ม → อย่าซ่อนแถว (ดู comment ข้างล่าง) */}
          <div
            className={`mh-search-results-list${
              isLoading && packageItems.length > 0 ? " is-loading-more" : ""
            }`}
          >
            {isLoading &&
              packageItems.length === 0 &&
              Array.from({ length: SKELETON_ROWS }).map((_, key) => (
                <div key={`skeleton-search-${key}`} className="mh-search-row-skeleton">
                  <span className="mh-search-skeleton mh-search-skeleton-thumb" />
                  <span style={{ flex: 1 }}>
                    <span className="mh-search-skeleton mh-search-skeleton-line" />
                    <span className="mh-search-skeleton mh-search-skeleton-line-short" />
                  </span>
                  <span className="mh-search-skeleton mh-search-skeleton-btn" />
                </div>
              ))}

            {!isLoading && packageItems.length === 0 && (
              <div className="mh-empty-state">
                <div className="mh-empty-state-icon">
                  <Icon name="search" style={{ margin: 0 }} />
                </div>
                <div className="mh-empty-state-text">
                  {debouncedSearch
                    ? `ไม่พบรายการตรวจ "${debouncedSearch}"`
                    : "ไม่พบรายการตรวจที่ค้นหา"}
                </div>
              </div>
            )}

            {/* ไม่ condition ด้วย isLoading: ถ้าซ่อนแถวทั้งหมดระหว่างโหลดหน้าเพิ่ม ความสูง
                ของลิสต์จะยุบ → scroll ของ panel ถูก clamp กลับขึ้นข้างบน staff อยู่ท้ายลิสต์
                อยู่แล้ว ต้องเลื่อนลงมาใหม่ทุกรอบที่กด "โหลดรายการเพิ่ม" */}
            {packageItems.map((item: any) => {
              const imageUrl = getResultImage(item);
              const cardKey = CartItemCard(item.id);
              const isItemLoading = !!props.addLoadingStatus?.[cardKey];
              const itemError = getCartErrorText(props.errorMessage?.[cardKey]);
              const productId = String(item.product_id ?? item.id);
              const quantity =
                cartQtyByProduct.get(productId) ?? cartQtyByProduct.get(String(item.id)) ?? 0;

              return (
                <div key={item.id} className="mh-search-result-row">
                  <div
                    className="mh-search-thumb"
                    style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined}
                  >
                    {!imageUrl && <Icon name="image outline" />}
                  </div>

                  <div className="mh-search-info">
                    <div className="mh-search-title">{item.name}</div>
                    <div className="mh-search-price">
                      {formatComma(item.price, "th-TH", false)} บาท
                    </div>
                    {item.price_full > item.price && (
                      <div className="mh-search-price-full">
                        {formatComma(item.price_full, "th-TH", false)} บาท
                      </div>
                    )}
                    {itemError && (
                      <div className="mh-search-item-error">
                        <div className="mh-search-item-error-text">{itemError.text}</div>

                        {itemError.detail && (
                          <button
                            type="button"
                            className="mh-search-item-error-toggle"
                            onClick={() =>
                              setErrorDetailKey(errorDetailKey === cardKey ? null : cardKey)
                            }
                          >
                            {errorDetailKey === cardKey ? "ปิด" : "รายละเอียด"}
                          </button>
                        )}
                      </div>
                    )}

                    {itemError?.detail && errorDetailKey === cardKey && (
                      <div className="mh-search-item-error-detail">{itemError.detail}</div>
                    )}
                  </div>

                  {quantity > 0 ? (
                    // เคยกด + ไปแล้ว → เปลี่ยนเป็น  − จำนวน +  ให้เพิ่ม/ลดได้จากบรรทัดนี้
                    // pending ยังกดต่อได้ — OptimisticCartQuantity + RunCartMutation
                    // ทำให้เลขขยับทันที และลำดับ PATCH ไม่ทับกัน (ดู StaffChatInterface)
                    <div className={`mh-search-quantity${isItemLoading ? " is-pending" : ""}`}>
                      <button
                        type="button"
                        className="mh-search-qty-btn"
                        aria-label={`ลดจำนวน ${item.name}`}
                        onClick={() => props.onDecreasePackage(Number(item.id), item)}
                      >
                        <Icon name="minus" />
                      </button>

                      <span className="mh-search-qty-value">{quantity}</span>

                      <button
                        type="button"
                        className="mh-search-qty-btn mh-search-qty-btn-add"
                        aria-label={`เพิ่มจำนวน ${item.name}`}
                        onClick={() => props.onAddPackage(Number(item.id), item)}
                      >
                        <Icon name="plus" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="mh-search-add-btn"
                      aria-label={`เพิ่ม ${item.name}`}
                      disabled={isItemLoading}
                      onClick={() => props.onAddPackage(Number(item.id), item)}
                    >
                      {isItemLoading ? (
                        <Icon name="circle notched" loading />
                      ) : (
                        <Icon name="plus" />
                      )}
                    </button>
                  )}
                </div>
              );
            })}

            {isLoading && packageItems.length > 0 && (
              <div className="mh-search-loading-more">กำลังโหลดรายการเพิ่ม…</div>
            )}

            {!!props.cartData?.packageHasMore && (
              <button
                type="button"
                className="mh-search-load-more"
                disabled={isLoading}
                onClick={() =>
                  props.onLoadMorePackages(
                    debouncedSearch,
                    props.cartData?.packageList?.length ?? packageItems.length,
                  )
                }
              >
                {isLoading ? "กำลังโหลด…" : "โหลดรายการเพิ่ม"}
              </button>
            )}
          </div>

          {/* สรุปตะกร้าของผู้รับบริการ (สิ่งที่ staff เพิ่มให้แล้ว และยังไม่ชำระ) */}
          {cartItems.length > 0 && (
            <div className="msg-cart-bar">
              <Icon name="shop" style={{ margin: 0 }} />
              ในตะกร้า {cartItems.length} รายการ · {formatComma(cartTotal, "th-TH", false)} บาท
              <button
                type="button"
                className="msg-cart-toggle"
                onClick={() => setShowCartItems(!showCartItems)}
              >
                {showCartItems ? "ซ่อน" : "ดูรายการ"}
              </button>
            </div>
          )}

          {showCartItems &&
            cartItems.map((item: any) => (
              <div key={`cart-${item.id}`} className="msg-cart-item">
                <span className="msg-cart-item-name">
                  {item.product_name || item.name || item.package_name}
                </span>
                <span className="msg-cart-item-qty">x{toWholeQuantity(item.quantity)}</span>
                <span className="msg-cart-item-price">
                  {formatComma(item.price ?? 0, "th-TH", false)} บาท
                </span>
              </div>
            ))}

          {/* ─── ส่ง link จ่ายเงินให้ลูกค้า ───
              GetCartPaymentLink (StaffChatInterface) → OfficialAccountDivisionDetail.retrieve ได้ oa id
              แล้วประกอบเป็น LINE_LIFF_URL?oa=<oa_id>&mehealthCheckout=true
              ฝั่งลูกค้า MobIsHealth เห็น mehealthCheckout=true → push "/package-payment/" (ตะกร้า+ชำระเงิน)
             StaffChat ส่งเข้าแชทด้วย chatController.postChatChannelMessage ตัวเดียวกับ ChatBox */}
          {cartItems.length > 0 && (
            <div className="msg-cart-payment">
              <button
                type="button"
                className="msg-cart-payment-btn"
                disabled={props.isSendingPaymentLink}
                onClick={() => setConfirmSendLink(true)}
              >
                <Icon
                  name={props.isSendingPaymentLink ? "circle notched" : "money"}
                  loading={props.isSendingPaymentLink}
                  style={{ margin: 0 }}
                />
                {props.isSendingPaymentLink ? "กำลังส่ง…" : "ส่ง link จ่ายเงินให้ลูกค้า"}
              </button>

              {paymentError && (
                <div className="mh-search-item-error">
                  <div className="mh-search-item-error-text">{paymentError.text}</div>

                  {paymentError.detail && (
                    <button
                      type="button"
                      className="mh-search-item-error-toggle"
                      onClick={() =>
                        setErrorDetailKey(
                          errorDetailKey === CartPaymentLinkCard ? null : CartPaymentLinkCard,
                        )
                      }
                    >
                      {errorDetailKey === CartPaymentLinkCard ? "ปิด" : "รายละเอียด"}
                    </button>
                  )}

                  {paymentError.detail && errorDetailKey === CartPaymentLinkCard && (
                    <div className="mh-search-item-error-detail">{paymentError.detail}</div>
                  )}
                </div>
              )}

              <ModConfirmCustom
                open={confirmSendLink}
                approveText="ส่งเข้าแชทลูกค้า"
                denyText="ยกเลิก"
                content={
                  <div style={{ margin: "0 0 -1.25rem" }}>
                    <label>ส่งลิงก์ชำระเงินของ MeHealth ให้ผู้รับบริการทางแชทนี้</label>
                    <div style={{ marginTop: "0.75rem", fontSize: "0.95rem" }}>
                      ในตะกร้า {cartCount} รายการ · {formatComma(cartTotal, "th-TH", false)} บาท
                    </div>
                    <div style={{ textAlign: "center", marginTop: "1.25rem" }}>
                      ต้องการส่งลิงก์ชำระเงินตอนนี้หรือไม่
                    </div>
                  </div>
                }
                onApprove={handleSendPaymentLink}
                onDeny={() => setConfirmSendLink(false)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CartSection;
